import { useCallback, useState } from 'react';

import { useForm, useFormyValidator } from '@givebacks/formy';
import { useCreatePaymentMethod, useUserData } from 'core/Api';
import {
  CardTokenizationFaqBadge,
  LearnMoreCardAccordion,
  ShopToGiveTermsCheckboxCard,
  StripeCardForm,
  StripeCardInfoForm,
  StripeSecurelyIntegratedWith,
} from 'core/Components';
import { getErrorMessage_v2 } from 'core/Utils';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Container,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  Title,
} from 'ui';
import { UseFormReturnType, isNotEmpty } from 'ui/Form';

import useGtmServices from '../../../../hooks/useGtmServices';
import './CardInfo.scss';

type Props = {
  onComplete: () => void;
  withTitle?: boolean;
  withBadge?: boolean;
  withFooterDisclaimer?: boolean;
};

const AddCardTitle = () => {
  return (
    // did not match default modal styles for title
    <Stack gap="xs" mb="10">
      <Title order={2} style={(theme) => ({ color: theme?.colors.gray[8] })}>
        In-store & Dining Program
      </Title>
      <Text fz="md" fw={500} c="gray">
        Add your{' '}
        <Anchor
          href="https://support.givebacks.com/hc/en-us/articles/15283251400343-What-cards-are-NOT-eligible-for-Shop-to-Give-"
          target="_blank"
        >
          eligible
        </Anchor>{' '}
        card so you can earn donations automatically at over 10,000 restaurants and retailers!
      </Text>
    </Stack>
  );
};

const AddMyCardButton = ({ onComplete }: { onComplete: () => void }) => {
  const { mutate: createPaymentMethod, isPending: isLoading } = useCreatePaymentMethod();
  const [error, setErrors] = useState<undefined | string>();
  const { useGetForm, isFormsValid } = useFormyValidator<{
    stripe_credit_card: StripeCardForm;
    terms: UseFormReturnType<{ terms: boolean }>;
  }>();
  const stripeForm = useGetForm('stripe_credit_card');
  const { data: userData } = useUserData();

  const onAddCard = useCallback(async () => {
    const { token, error } = await stripeForm.getTransformedValues();
    if (error) {
      setErrors('Invalid card');
      return;
    }
    createPaymentMethod(
      {
        email: userData?.email ?? null,
        card_token: token,
        save_for_future_use: false,
        enroll_in_shop_to_give: true,
      },
      {
        onSuccess: () => {
          onComplete();
        },
        onError: (error) => {
          setErrors(getErrorMessage_v2(error));
        },
      }
    );
  }, [createPaymentMethod, userData?.email, stripeForm, onComplete]);

  return (
    <>
      <Alert.Transition title={error} type="error" mounted={Boolean(error)} />

      <Button onClick={onAddCard} fullWidth disabled={!isFormsValid}>
        Add My Card
      </Button>

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{ size: 'lg', color: 'violet', variant: 'dots' }}
      />
    </>
  );
};

const AddCardNew = ({
  onComplete,
  withTitle = true,
  withBadge = false,
  withFooterDisclaimer = true,
}: Props) => {
  const GtmService = useGtmServices();
  const [isLearMoreOpen, setIsLearnMoreOpen] = useState(false);

  const form = useForm(
    { initialValues: { terms: false }, validate: { terms: isNotEmpty('Terms are required') } },
    {
      id: 'terms',
    }
  );

  return (
    <>
      <form autoComplete="on" className="StripeCard">
        <Stack gap="md" align="stretch">
          {withTitle && <AddCardTitle />}
          <Stack>
            <StripeSecurelyIntegratedWith />
            <StripeCardInfoForm />
            {withBadge && <CardTokenizationFaqBadge />}
          </Stack>
          <ShopToGiveTermsCheckboxCard
            checkboxFieldProps={form.getInputProps('terms', { type: 'checkbox' })}
          />
          <AddMyCardButton onComplete={onComplete} />
        </Stack>

        {withFooterDisclaimer && (
          <Container mt={8} fluid px={0}>
            <Text c="gray" ta="center" fz="sm" fw="500" w={'100%'} mb="md">
              We’re serious about security. Learn more about our{' '}
              <Anchor
                onClick={() =>
                  GtmService.click(() => setIsLearnMoreOpen(true), {
                    type: 'button',
                    action: 'info',
                    label: 'Learn More about our commitment',
                  })
                }
                fz="sm"
                fw={500}
              >
                commitment
              </Anchor>{' '}
              to keep your data safe.{' '}
            </Text>
          </Container>
        )}
      </form>
      <Modal
        title="Learn More about the Givebacks Promise"
        styles={{
          content: { display: 'flex', flexDirection: 'column' },
          body: { display: 'flex', flexGrow: 1 },
        }}
        opened={isLearMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        zIndex={201}
        variant="full-screen-mobile"
      >
        <Stack justify="space-between" style={{ flexGrow: 1 }}>
          <LearnMoreCardAccordion variant="separated" />
          <Box>
            <Button onClick={() => setIsLearnMoreOpen(false)} variant="subtle">
              Back
            </Button>
          </Box>
        </Stack>
      </Modal>
    </>
  );
};

interface AddCardModal extends Props {
  opened: boolean;
  onClose: () => void;
}
const AddCardModal = ({ opened, onClose, onComplete }: AddCardModal) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      variant="full-screen-mobile"
      title={<AddCardTitle />}
      styles={{
        header: { alignItems: 'flex-start', position: 'relative' },
        close: { flexShrink: 0 },
      }}
    >
      <AddCardNew onComplete={onComplete} withTitle={false} />
    </Modal>
  );
};

export { AddCardModal, AddCardNew };
